import React, { useCallback, useEffect, useState } from 'react';
import { StepControls } from '~/common/step-controls';
import { useSelector } from 'react-redux';
import { selectNewSong, selectUserData } from '~/features/dashboard/store/selectors';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { Controller, useForm } from 'react-hook-form';
import { INewSongStepTwoFormInputs } from '~/models/music/new-song/formInputs';
import { createNewSongStepTwoThunk } from '~/features/dashboard/store/actions.thunks';
import { TextInput, NumberInput } from '~/features/dashboard/components/add-deal/components/text-input';
import { NamedForm } from '~/features/dashboard/components/add-song/components/named-form';
import { Grid, TextField, Typography } from '@material-ui/core';
import css from '~/features/dashboard/components/add-song/components/step-2/styles.module.scss';
import { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete/Autocomplete';
import classNames from 'classnames';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {isSongFormEditing} from "~/features/dashboard/store/actions";
import { useProfile } from '~/utils/hooks/useProfile';

interface Step_2Props {
    prevStep: () => void;
}

export const Step_2: React.FC<Step_2Props> = ({ prevStep }) => {
    const newSongState = useSelector(selectNewSong);
    const dispatch = useThunkDispatch();
    const user = useSelector(selectUserData);

    const [defaultWriterName] = useProfile('defaultWriterName');
    const [defaultPublisherName] = useProfile('defaultPublisherName');
    const [affiliation] = useProfile('affiliation');
    const [iPINumber] = useProfile('iPINumber');
    const defaultCAECode = [affiliation, iPINumber].join(' - ');

    const {
        handleSubmit,
        register,
        errors,
        formState: { isDirty },
        control,
        setValue,
    } = useForm<INewSongStepTwoFormInputs>({
        defaultValues: {
            CAECode: newSongState?.gSongCode2 || '',
            writingShare: newSongState?.gWriterShare || 0,
            writerName: newSongState?.gMyWriterName || '',
            publisherName: newSongState?.gPublisherName || '',
            publishingshare: newSongState?.gPublishingShare || 0,
        },
    });

    const fillWithDefaults = () => {
        setValue('writerName', String(defaultWriterName) || '', { shouldDirty: true });
        setValue('publisherName', String(defaultPublisherName) || '', { shouldDirty: true });
        setValue('CAECode', String(defaultCAECode) || '', { shouldDirty: true });
    };

    const onSubmit = useCallback(
        (data: INewSongStepTwoFormInputs) => {
            dispatch(createNewSongStepTwoThunk({ ...data, isDirty }));
        },
        [dispatch, isDirty]
    );

    const [ipiList, setIpiList] = useState<string[]>([]);

    useEffect(() => {
        let data: any = [];
        user?.ipiList.map(ipi => {
            data.push(`${ipi.affiliation} - ${ipi.iPNumber}`);
        });
        setIpiList(data);
        dispatch(isSongFormEditing(true));
        // eslint-disable-next-line
    }, [user]);

    return (
        <NamedForm name={newSongState?.gSongName} onSubmit={handleSubmit(onSubmit)} action={{ label: 'Set Defaults', handler: fillWithDefaults }}>
            <Grid className={css.grid} container spacing={3}>
                <Grid item xs={12}>
                    <Typography className="publish-title" variant="h6">
                        Enter My Publishing Split
                    </Typography>
                </Grid>
                <Grid className={css.grid} item xs={6}>
                    <Typography className={classNames(css.bold, css.infoTitle)}>My CAE Code</Typography>
                    <Controller
                        render={props => (
                            <Autocomplete
                                {...props}
                                options={ipiList}
                                className={css.textInput}
                                fullWidth
                                renderInput={(p: AutocompleteRenderInputParams) => (
                                    <TextField className={classNames(css.textInput, css.textSociety)} {...p} autoFocus variant="filled" value={props.value} />
                                )}
                                onChange={(_, data) => props.onChange(data)}
                            />
                        )}
                        name={`CAECode`}
                        control={control}
                    />
                </Grid>
                <Grid className={css.grid} item xs={6} />
                <Grid className={css.grid} item xs={6}>
                    <TextInput
                        className={css.textInput}
                        label={'My Writer Name'}
                        name={'writerName'}
                        register={register({ required: 'Writer Name is required' })}
                        fieldError={errors.writerName}
                    />
                </Grid>
                <Grid className={css.grid} item xs={6}>
                    <NumberInput
                        className={css.textInput}
                        type="number"
                        label={'My Writer Share'}
                        name={'writingShare'}
                        register={register({ required: 'Writer Share is required' })}
                        fieldError={errors.writingShare}
                        min={0}
                        max={100}
                        step={0.01}
                    />
                </Grid>
                <Grid className={css.grid} item xs={6}>
                    <TextInput
                        className={css.textInput}
                        label={'Known Publisher'}
                        name={'publisherName'}
                        register={register}
                        fieldError={errors.publisherName}
                    />
                </Grid>
                <Grid className={css.grid} item xs={6}>
                    <NumberInput
                        className={css.textInput}
                        type="number"
                        label={'My Publisher Share'}
                        name={'publishingshare'}
                        register={register({ required: 'Publisher Share is required' })}
                        fieldError={errors.publishingshare}
                        min={0}
                        max={100}
                        step={0.01}
                    />
                </Grid>
            </Grid>
            <StepControls className={css.nextButton} onPrevClick={prevStep} />
        </NamedForm>
    );
};
